/**
 * UGC videos row (sections/ugc-videos.liquid). Every clip is muted and loads nothing until it
 * is seen: the posters (data-poster) arrive once the row comes within a screen of the viewport, and
 * a clip plays by itself while most of it is in view. A tapped clip plays while any of
 * it is on screen, and every clip pauses (and mutes) once it leaves. Tapping a clip
 * turns its sound on (and every other clip's off); tapping again mutes it.
 *
 * With reduced motion nothing plays by itself. The tap button becomes a play button: it plays
 * the clip with sound, and pauses it again. A clip scrolled out of view pauses either way.
 */
import { defineElement, prefersReducedMotion } from '@wispaire/global';

const Base = globalThis.HTMLElement ?? class {};
const AUTOPLAY_RATIO = 0.6;

export class UgcVideos extends Base {
  connectedCallback() {
    this.reduced = prefersReducedMotion();
    this.toggleAttribute('data-reduced-motion', this.reduced);
    this.clips = [...this.querySelectorAll('[data-clip]')].map((el) => ({
      el,
      video: el.querySelector('video'),
      button: el.querySelector('button'),
      ratio: 0,
      chosen: false,
    }));

    this.near = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        for (const { video } of this.clips) video.poster ||= video.dataset.poster;
        this.near.disconnect();
      },
      { rootMargin: '100% 0px' }
    );
    this.near.observe(this);

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const { target, isIntersecting, intersectionRatio } of entries) {
          const clip = this.clips.find((candidate) => candidate.el === target);
          clip.ratio = isIntersecting ? intersectionRatio : 0;
          if (!clip.ratio) clip.chosen = false;
          this.sync(clip);
        }
      },
      // 0 reports a clip leaving the screen, 0.6 the share at which it plays by itself.
      { threshold: [0, AUTOPLAY_RATIO] }
    );

    for (const clip of this.clips) {
      clip.button.addEventListener('click', () => this.tap(clip));
      this.observer.observe(clip.el);
      this.sync(clip);
    }
  }

  disconnectedCallback() {
    this.near?.disconnect();
    this.observer?.disconnect();
  }

  /** Motion: the tap chooses which clip speaks. Reduced motion: it chooses which clip plays. */
  tap(clip) {
    const chosen = !clip.chosen;
    for (const other of this.clips) {
      other.chosen = other === clip && chosen;
      this.sync(other);
    }
  }

  /** Brings one clip's video and button in line with its state. */
  sync({ video, button, ratio, chosen }) {
    const playing = ratio > 0 && (chosen || (!this.reduced && ratio >= AUTOPLAY_RATIO));
    video.muted = !chosen;
    if (playing && video.paused) video.play()?.catch(() => {});
    if (!playing && !video.paused) video.pause();
    button.setAttribute('aria-pressed', String(chosen));
    button.setAttribute('aria-label', this.reduced ? button.dataset.labelPlay : button.dataset.labelSound);
  }
}

defineElement('ugc-videos', UgcVideos);
